import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, onAuthStateChanged, getIdToken } from 'firebase/auth';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import B2BCheckoutView from '../components/B2BCheckoutView';
import B2BOnboardingModal from '../components/B2BOnboardingModal';
import B2BDataGrid from '../components/B2BDataGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend-core-644060845776.us-central1.run.app';

const UpgradeBanner = ({ onUpgrade }) => {
  const { t } = useTranslation();
  return (
    <div className="glass-panel" style={{ padding: '25px', textAlign: 'center', marginBottom: '30px', border: '2px solid #EF4444', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '12px', boxShadow: '0 4px 15px rgba(239, 68, 68, 0.2)' }}>
      <h3 style={{ margin: '0 0 10px 0', color: '#EF4444', fontSize: '1.4rem' }}>{t('b2b.paywall_banner_title')}</h3>
      <p style={{ margin: '0 0 20px 0', color: 'var(--text-secondary)', fontSize: '1.05rem' }}>{t('b2b.paywall_banner_subtitle')}</p>
      <button onClick={onUpgrade} className="btn-primary" style={{ background: '#EF4444', border: 'none', padding: '12px 24px', fontSize: '1.05rem', fontWeight: 'bold' }}>
        {t('b2b.paywall_button')}
      </button>
    </div>
  );
};

// InvoiceList replaced by B2BDataGrid — see components/B2BDataGrid.js

const B2BPortal = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  
  // Default to previous month
  const [billingMonth, setBillingMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7); // "YYYY-MM"
  });

  const [loadingDownload, setLoadingDownload] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [magicInboxEmail, setMagicInboxEmail] = useState("");
  const [dashboardMetrics, setDashboardMetrics] = useState({ queued: 0, processed: 0, exceptions: 0 });
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [freeQuotaRemaining, setFreeQuotaRemaining] = useState(1);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [documents, setDocuments] = useState([]);
  const auth = getAuth();

  // ── Client-side metric derivation (source of truth = documents array) ──────
  // Overrides the backend-provided counts to prevent API Contract Drift.
  // Rules:
  //   • ATTACHMENT → excluded from every count (ghost files)
  //   • processed  = COMPLETED only
  //   • exceptions = FAILED + MANUAL_REVIEW
  //   • queued     = PENDING + REQUIRES_UPGRADE + any other non-terminal state
  const derivedMetrics = useMemo(() => {
    let processed = 0, exceptions = 0, queued = 0;
    for (const doc of documents) {
      const s = doc.extraction_status;
      if (s === 'ATTACHMENT') continue;          // ghost — never counted
      if (s === 'COMPLETED') processed++;
      else if (s === 'FAILED' || s === 'MANUAL_REVIEW') exceptions++;
      else queued++;                             // PENDING | REQUIRES_UPGRADE | …
    }
    return { processed, exceptions, queued };
  }, [documents]);

  // Merge: prefer client-derived counts when documents are loaded;
  // fall back to server metrics (e.g. on first render before documents arrive).
  const displayMetrics = documents.length > 0 ? derivedMetrics : dashboardMetrics;

  const navigate = useNavigate();

  const fetchDashboardSummary = async (currentUser) => {
    try {
      setLoadingDashboard(true);
      const token = await getIdToken(currentUser);
      const url = `${BACKEND_URL}/api/b2b/dashboard/summary`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        // Fix for missing magic_inbox string or nested data updates
        const inbox = response.data.magic_inbox || response.data.dashboardData?.magic_inbox || response.data.magic_inbox_alias || response.data.dashboardData?.magic_inbox_alias || "";
        setMagicInboxEmail(inbox);
        
        if (!inbox) {
          setShowOnboarding(true);
        } else {
          setShowOnboarding(false);
        }
        
        if (response.data.metrics || response.data.dashboardData?.metrics) {
          setDashboardMetrics(response.data.metrics || response.data.dashboardData.metrics);
        }
        setFreeQuotaRemaining(response.data.free_quota_remaining ?? 1);
        setSubscriptionStatus(response.data.subscription_status || 'free');
        setDocuments(response.data.documents || []);
      }
    } catch (err) {
      console.error("Error fetching dashboard summary:", err);
      if (err.response && (err.response.status === 403 || err.response.data?.error === 'subscription_required')) {
        setMagicInboxEmail("");
        setDashboardMetrics({ queued: 0, processed: 0, exceptions: 0 });
        setShowCheckout(true);
        setShowOnboarding(false);
      } else if (err.response?.status === 404 || err.response?.data?.error?.includes('profile')) {
        setShowOnboarding(true);
      }
    } finally {
      setLoadingDashboard(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          const role = (idTokenResult.claims?.role || '').toUpperCase();
          if (role !== 'B2B' && role !== 'ADMIN') {
            navigate('/');
            return;
          }
          setUser(currentUser);
          fetchDashboardSummary(currentUser);
        } catch (err) {
          console.error("Error checking claims", err);
          navigate('/');
        }
      } else {
        setUser(null);
        setLoadingDashboard(false);
      }
      setLoadingUser(false);
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [auth, navigate]);

  const handleDownload = async () => {
    if (!user) return;
    setErrorMsg(null);
    setLoadingDownload(true);

    try {
      const token = await getIdToken(user);
      const url = `${BACKEND_URL}/api/b2b/closures/${billingMonth}/download`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.download_url) {
        // Redirigir a la URL firmada
        window.location.href = response.data.download_url;
      } else {
        setErrorMsg(t('b2b.error_unexpected'));
      }
    } catch (err) {
      console.error("Error downloading closure:", err);
      if (err.response?.status === 404 || err.response?.status === 403) {
        setErrorMsg(t('b2b.error_not_available'));
      } else {
        setErrorMsg(t('b2b.error_generic'));
      }
      // Ocultar mensaje de error dsp de 5 segs
      setTimeout(() => setErrorMsg(null), 5000);
    } finally {
      setLoadingDownload(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(magicInboxEmail);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (loadingUser) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--text-secondary)' }}>Cargando...</div>;
  }

  if (!user) {
    return (
      <div style={{ padding: '20px', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div className="glass-panel" style={{ padding: '40px', maxWidth: '400px', width: '100%' }}>
          <div className="logo-text" style={{ marginBottom: '20px' }}>On<span className="logo-accent">Track</span> B2B</div>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '30px' }}>{t('b2b.login_required')}</p>
          <Link to="/?redirect=/b2b" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-block' }}>{t('b2b.go_to_login')}</Link>
        </div>
      </div>
    );
  }

  if (showCheckout) {
    return <B2BCheckoutView user={user} />;
  }

  return (
    <div style={{ padding: '20px', paddingTop: 'calc(40px + env(safe-area-inset-top, 0px))', fontFamily: "'Inter', system-ui, sans-serif", maxWidth: '800px', margin: '0 auto', minHeight: '100vh' }}>
      
      {showOnboarding && !loadingDashboard && (
        <B2BOnboardingModal onComplete={() => fetchDashboardSummary(user)} />
      )}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
        <div className="logo-text" style={{ fontSize: '1.8rem' }}>On<span className="logo-accent">Track</span> <span style={{fontSize: '1.2rem', color: 'var(--text-secondary)', fontWeight: 'normal'}}>B2B</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{user.displayName?.split(' ')[0]}</p>
          <button onClick={() => { auth.signOut(); navigate('/'); }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', cursor: 'pointer', padding: '5px 10px', borderRadius: '6px', fontSize: '0.8rem' }}>{t('app.logout')}</button>
        </div>
      </div>

      {/* Hero Section: Magic Inbox */}
      <div className="glass-panel" style={{ padding: '30px', textAlign: 'center', marginBottom: '30px', border: '1px solid rgba(79, 70, 229, 0.4)', background: 'rgba(79, 70, 229, 0.05)' }}>
        <h2 style={{ marginBottom: '15px', fontSize: '1.6rem', color: 'var(--text-primary)' }}>{t('b2b.dashboard.inbox_title')}</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '1rem' }}>
          {t('b2b.dashboard.inbox_subtitle')}
        </p>
        <div style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(0,0,0,0.3)', padding: '10px 20px', borderRadius: '12px', border: '1px solid var(--glass-border)', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ fontSize: '1.05rem', fontWeight: '500', color: 'var(--text-primary)', letterSpacing: '0.5px' }}>
            {loadingDashboard ? 'Cargando...' : (magicInboxEmail || 'Pendiente / No asignado')}
          </span>
          <button 
            onClick={copyToClipboard}
            className="btn-primary"
            style={{ padding: '8px 16px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px', justifyContent: 'center' }}
            disabled={loadingDashboard || !magicInboxEmail}
          >
            {isCopied ? (
              <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg> {t('b2b.dashboard.copied_btn')}</>
            ) : (
              <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> {t('b2b.dashboard.copy_btn')}</>
            )}
          </button>
        </div>
      </div>

      {/* Flujo de Instrucciones */}
      <div style={{ marginBottom: '40px' }}>
        <h3 style={{ marginBottom: '20px', fontSize: '1.2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('b2b.how_it_works')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div className="glass-panel" style={{ padding: '25px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(79, 70, 229, 0.2)', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px', fontSize: '1.2rem', fontWeight: 'bold' }}>1</div>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-primary)', margin: 0 }}>{t('b2b.instruction_step1')}</p>
          </div>
          <div className="glass-panel" style={{ padding: '25px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(79, 70, 229, 0.2)', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px', fontSize: '1.2rem', fontWeight: 'bold' }}>2</div>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-primary)', margin: 0 }}>{t('b2b.instruction_step2')}</p>
          </div>
          <div className="glass-panel" style={{ padding: '25px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(79, 70, 229, 0.2)', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px', fontSize: '1.2rem', fontWeight: 'bold' }}>3</div>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-primary)', margin: 0 }}>{t('b2b.instruction_step3')}</p>
          </div>
        </div>
      </div>

      {/* Telemetría (Métricas Reales) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '40px' }}>
        <div className="glass-panel" style={{ padding: '25px 20px', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '10px' }}>{t('b2b.dashboard.metrics_queued')}</p>
          <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: '0', color: '#FCD34D' }}>
            {loadingDashboard ? (
              <span className="spinner" style={{display: 'inline-block', width: '24px', height: '24px', border: '3px solid rgba(252, 211, 77, 0.3)', borderTopColor: '#FCD34D', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></span>
            ) : displayMetrics.queued}
          </p>
        </div>
        <div className="glass-panel" style={{ padding: '25px 20px', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '10px' }}>{t('b2b.dashboard.metrics_processed')}</p>
          <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: '0', color: '#34D399' }}>
            {loadingDashboard ? (
              <span className="spinner" style={{display: 'inline-block', width: '24px', height: '24px', border: '3px solid rgba(52, 211, 153, 0.3)', borderTopColor: '#34D399', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></span>
            ) : displayMetrics.processed}
          </p>
        </div>
        <div className="glass-panel" style={{ padding: '25px 20px', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '10px' }}>Excepciones / Revisión</p>
          <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: '0', color: '#EF4444' }}>
            {loadingDashboard ? (
              <span className="spinner" style={{display: 'inline-block', width: '24px', height: '24px', border: '3px solid rgba(239, 68, 68, 0.3)', borderTopColor: '#EF4444', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></span>
            ) : displayMetrics.exceptions}
          </p>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {(freeQuotaRemaining <= 0 && subscriptionStatus !== 'active' && subscriptionStatus !== 'premium') && (
        <UpgradeBanner onUpgrade={() => setShowCheckout(true)} />
      )}

      <B2BDataGrid documents={documents} />

      {/* Módulo Base: Descargar Cierres */}
      <div className="glass-panel" style={{ padding: '30px', textAlign: 'center' }}>
        <h2 style={{ marginBottom: '10px', fontSize: '1.5rem' }}>{t('b2b.dashboard.monthly_closures')}</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '30px', fontSize: '0.95rem' }}>
          {t('b2b.subtitle')}
        </p>

        <div style={{ marginBottom: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="billing-month" style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>{t('b2b.billing_month')}</label>
          <input 
            type="month" 
            id="billing-month"
            value={billingMonth}
            onChange={(e) => setBillingMonth(e.target.value)}
            style={{
              background: 'rgba(0,0,0,0.2)',
              border: '1px solid var(--glass-border)',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              fontSize: '1rem',
              colorScheme: 'dark', // Para que el calendario nativo sea oscuro
              outline: 'none',
              cursor: 'pointer'
            }}
          />
        </div>

        <button 
          onClick={handleDownload} 
          disabled={loadingDownload || !billingMonth}
          className="btn-primary" 
          style={{ width: '100%', padding: '14px', fontSize: '1.05rem', opacity: (loadingDownload || !billingMonth) ? 0.7 : 1 }}
        >
          {loadingDownload ? t('b2b.generating') : t('b2b.download_btn')}
        </button>
      </div>

      {errorMsg && (
        <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', background: '#EF4444', color: 'white', padding: '12px 24px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)', zIndex: 50, whiteSpace: 'nowrap' }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
};

export default B2BPortal;
